import { getDatabase, ref, get, update, push } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { formatDateForStorage, formatDateOnlyForStorage, addInterval, DateInterval } from '../Helpers/DateUtils';
import { requireAdmin } from './adminGuard';
import { recordActivity } from './activityApi';
import {
    ASSESSMENT_GROUP_KEYS,
    ASSESSMENT_GROUPS,
    AssessmentGroupKey,
    AssessmentScheduleDrafts,
    assessmentTemplateId,
    assessmentTemplateName,
    DEFAULT_ASSESSMENT_INTERVAL
} from '../Helpers/assessmentGroups';
import { BuildingCheckTemplate, InspectionCategory } from '../Pages/BuildingChecks/types';
import { parseBuildingCheck } from '../Pages/BuildingChecks/buildingChecksUtils';
import type { BuildingCheck, CheckCompletion } from '../Pages/BuildingChecks/buildingChecksUtils';

/** Templates keyed by group; a group is absent while its template does not exist. */
export type AssessmentTemplates = Partial<Record<AssessmentGroupKey, BuildingCheckTemplate>>;

/** Fields a new check is created with; the API adds addedBy and addedTime. */
export type NewBuildingCheck = Omit<BuildingCheck, 'addedBy' | 'addedTime' | 'lastCompletedAt' | 'lastCompletedBy' | 'lastMeasurement' | 'snoozedUntil' | 'snoozedBy' | 'snoozedAt'>;

/** Editable fields of an existing check; null clears a field. templateId and componentKey are fixed. */
export type BuildingCheckChanges = {
    [K in 'nextDueDate' | 'startDate' | 'label' | 'buildingNotes' | 'customInterval' | 'customCost'
        | 'unitKey' | 'unitLabel' | 'location']?: BuildingCheck[K] | null;
};

/** Web quick completion through the completeInspection callable (no per-component review). */
export interface QuickCompletionInput {
    buildingKey: string;
    checkKey: string;
    /** YYYY-MM-DD, not in the future. */
    completedDate: string;
    summaryComment: string;
    measurementValue?: number;
    /** Display name of the check, stored on the completion as checkLabel. */
    checkLabel: string;
}

/** A completion record with its date key. */
export interface DatedCheckCompletion extends CheckCompletion {
    date: string;
}

/** Input for the pure activation path map, see `assessmentInstanceUpdates`. */
export interface AssessmentInstanceUpdatesInput {
    buildingKey: string;
    /** Groups with live components in the building. */
    groups: AssessmentGroupKey[];
    /** The groups the user edited in the activation dialog. */
    staged: AssessmentScheduleDrafts;
    /** Existing checks, `inspections/active/{b}`, keyed by check key (a Skickbedömning key equals its template ID). */
    instances: Record<string, BuildingCheck | undefined>;
    templates: AssessmentTemplates;
    uid: string;
    /** YYYY-MM-DD */
    today: string;
    /** YYYY-MM-DD hh:mm:ss */
    now: string;
}

/**
 * Path map that schedules Skickbedömning when a maintenance plan is activated.
 * Merged into the activation's atomic root update by `useActivatePlan`.
 *
 * - Any of the three templates missing → empty map (the plan activates, nothing is scheduled; D5).
 * - Group with live components and no instance → the whole instance is written,
 *   due `staged.nextDate ?? today + (staged.customInterval ?? template.interval)`.
 * - Instance exists and the group has a staged entry → `customInterval` is set
 *   to the staged value (null clears an override) and `nextDueDate` moves to the
 *   staged date, clearing the snooze when it changes. `lastCompletedAt/By` untouched.
 * - Instance exists and nothing is staged → untouched. A staged entry exists
 *   only after a user edit (D16), so an untouched dialog never clears an override.
 */
export const assessmentInstanceUpdates = (input: AssessmentInstanceUpdatesInput): Record<string, unknown> => {
    const { buildingKey, groups, staged, instances, templates, uid, today, now } = input;
    if (ASSESSMENT_GROUP_KEYS.some(group => !templates[group])) {
        return {};
    }

    const updates: Record<string, unknown> = {};
    for (const group of groups) {
        const template = templates[group]!;
        const templateId = assessmentTemplateId(group);
        const base = `inspections/active/${buildingKey}/${templateId}`;
        const instance = instances[templateId];
        const stagedEntry = staged[group];

        if (!instance) {
            const interval = stagedEntry?.customInterval ?? template.interval;
            const nextDueDate = stagedEntry?.nextDate ?? addInterval(today, interval);
            const created: BuildingCheck = {
                templateId,
                startDate: today,
                nextDueDate,
                addedBy: uid,
                addedTime: now,
                ...(stagedEntry?.customInterval && { customInterval: stagedEntry.customInterval })
            };
            updates[base] = created;
            continue;
        }

        if (!stagedEntry) continue;

        updates[`${base}/customInterval`] = stagedEntry.customInterval ?? null;
        if (stagedEntry.nextDate && stagedEntry.nextDate !== instance.nextDueDate) {
            updates[`${base}/nextDueDate`] = stagedEntry.nextDate;
            updates[`${base}/snoozedUntil`] = null;
            updates[`${base}/snoozedBy`] = null;
            updates[`${base}/snoozedAt`] = null;
        }
    }
    return updates;
};

const parseTemplate = (id: string, data: unknown): BuildingCheckTemplate | null => {
    if (!data || typeof data !== 'object') return null;
    const d = data as Record<string, unknown>;
    if (typeof d.name !== 'string' || !d.interval || typeof d.interval !== 'object') {
        console.warn(`Skipping malformed inspection template ${id}`);
        return null;
    }
    return { ...(d as unknown as BuildingCheckTemplate), id };
};

const parseInterval = (value: unknown): DateInterval | null => {
    if (!value || typeof value !== 'object') return null;
    const v = value as Record<string, unknown>;
    if (typeof v.value !== 'number' || v.value < 1) return null;
    if (v.unit !== 'days' && v.unit !== 'weeks' && v.unit !== 'months' && v.unit !== 'years') return null;
    return { value: v.value, unit: v.unit };
};

export class BuildingChecksApi {
    private db = getDatabase();

    private getCurrentUser() {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad');
        }
        return user;
    }

    /**
     * Verify that the current user is an admin
     */
    private async verifyAdmin(): Promise<void> {
        const user = this.getCurrentUser();
        const adminRef = ref(this.db, `users/userList/${user.uid}/admin`);
        const adminSnapshot = await get(adminRef);
        if (!adminSnapshot.exists() || adminSnapshot.val() !== true) {
            throw new Error('Du måste vara administratör för att utföra denna åtgärd');
        }
    }

    // ============ SNOOZE OPERATIONS ============

    /**
     * Snooze a building check until a specific date (admin only)
     * - Sets snoozedUntil, snoozedBy, snoozedAt fields
     * - Check will be hidden from active list until snoozedUntil date arrives
     */
    async snoozeBuildingCheck(
        buildingId: string,
        checkKey: string,
        snoozeDate: string
    ): Promise<void> {
        await this.verifyAdmin();
        const user = this.getCurrentUser();

        // Validate snooze date is in the future
        const today = formatDateOnlyForStorage(new Date());
        if (snoozeDate <= today) {
            throw new Error('Snooze-datum måste vara i framtiden');
        }

        const checkRef = ref(this.db, `inspections/active/${buildingId}/${checkKey}`);
        const snapshot = await get(checkRef);

        if (!snapshot.exists()) {
            throw new Error('Kontroll hittades inte');
        }

        await update(checkRef, {
            snoozedUntil: snoozeDate,
            snoozedBy: user.uid,
            snoozedAt: formatDateForStorage(new Date())
        });
        recordActivity('inspection.updated', { buildingKey: buildingId, targetKey: checkKey, label: snapshot.val()?.label });
    }

    /**
     * Unsnooze a building check, making it visible in the active list again (admin only)
     * - Clears snoozedUntil, snoozedBy, snoozedAt fields
     */
    async unsnoozeBuildingCheck(
        buildingId: string,
        checkKey: string
    ): Promise<void> {
        await this.verifyAdmin();

        const checkRef = ref(this.db, `inspections/active/${buildingId}/${checkKey}`);
        const snapshot = await get(checkRef);

        if (!snapshot.exists()) {
            throw new Error('Kontroll hittades inte');
        }

        await update(checkRef, {
            snoozedUntil: null,
            snoozedBy: null,
            snoozedAt: null
        });
        recordActivity('inspection.updated', { buildingKey: buildingId, targetKey: checkKey, label: snapshot.val()?.label });
    }

    // ============ SKICKBEDÖMNING (condition assessment) ============

    /** The three system templates that exist, keyed by group. */
    async loadAssessmentTemplates(): Promise<AssessmentTemplates> {
        const snapshots = await Promise.all(
            ASSESSMENT_GROUP_KEYS.map(group => get(ref(this.db, `inspections/templates/${assessmentTemplateId(group)}`)))
        );
        const templates: AssessmentTemplates = {};
        ASSESSMENT_GROUP_KEYS.forEach((group, i) => {
            const snapshot = snapshots[i];
            if (!snapshot.exists()) return;
            const template = parseTemplate(assessmentTemplateId(group), snapshot.val());
            if (template) templates[group] = template;
        });
        return templates;
    }

    /** Groups whose system template does not exist yet. */
    async missingAssessmentTemplateGroups(): Promise<AssessmentGroupKey[]> {
        const templates = await this.loadAssessmentTemplates();
        return ASSESSMENT_GROUP_KEYS.filter(group => !templates[group]);
    }

    /**
     * Create any missing system template from code defaults (admin or superadmin).
     * Idempotent: existing templates are never touched. Interval and description
     * are seeded from the legacy `config/conditionAssessment/{group}` node while
     * it exists. Returns the groups that were created. Run only from the instance
     * setup surface and the Settings tab, never on page open (D5, D28).
     */
    async ensureAssessmentTemplates(): Promise<AssessmentGroupKey[]> {
        await requireAdmin(this.db, 'skapa mallar för skickbedömning');
        const missing = await this.missingAssessmentTemplateGroups();
        if (missing.length === 0) return [];

        const legacySnapshots = await Promise.all(
            missing.map(group => get(ref(this.db, `config/conditionAssessment/${group}`)))
        );

        const now = formatDateForStorage(new Date());
        const updates: Record<string, unknown> = {};
        missing.forEach((group, i) => {
            const legacy = legacySnapshots[i].exists()
                ? (legacySnapshots[i].val() as { interval?: unknown; instruction?: unknown })
                : undefined;
            const id = assessmentTemplateId(group);
            const template: BuildingCheckTemplate = {
                id,
                name: assessmentTemplateName(group),
                category: InspectionCategory.ConditionAssessment,
                description: typeof legacy?.instruction === 'string' ? legacy.instruction : '',
                componentBaseTypeGroup: group,
                system: true,
                interval: parseInterval(legacy?.interval) ?? DEFAULT_ASSESSMENT_INTERVAL,
                nextDueDateCalculation: 'actual',
                photoRequired: false,
                estimatedCost: 0,
                estimatedTime: 0,
                advanceNotice: 0,
                createdAt: now,
                updatedAt: now
            };
            updates[`inspections/templates/${id}`] = template;
        });

        await update(ref(this.db), updates);
        recordActivity('settings.updated');
        return missing;
    }

    /** All checks of a building, keyed by check key (Skickbedömning: `conditionAssessment_{group}`). */
    async loadBuildingCheckInstances(buildingKey: string): Promise<Record<string, BuildingCheck>> {
        const snapshot = await get(ref(this.db, `inspections/active/${buildingKey}`));
        if (!snapshot.exists()) return {};
        const data = snapshot.val() as Record<string, unknown>;
        const instances: Record<string, BuildingCheck> = {};
        for (const [checkKey, value] of Object.entries(data)) {
            const check = parseBuildingCheck(checkKey, value);
            if (check) instances[checkKey] = check;
        }
        return instances;
    }

    /** All inspection templates, keyed by ID. Malformed templates are skipped. */
    async loadTemplates(): Promise<Record<string, BuildingCheckTemplate>> {
        const snapshot = await get(ref(this.db, 'inspections/templates'));
        if (!snapshot.exists()) return {};
        const templates: Record<string, BuildingCheckTemplate> = {};
        for (const [id, value] of Object.entries(snapshot.val() as Record<string, unknown>)) {
            const template = parseTemplate(id, value);
            if (template) templates[id] = template;
        }
        return templates;
    }

    /**
     * Create several checks on a building in one multi-path update, each under its own
     * push key; returns the keys in order. Typically one check per component of a
     * template, sharing schedule and overrides. Callers check overlap first (a component
     * is in at most one check per template). Skickbedömning is never created here.
     */
    async createBuildingChecks(buildingKey: string, checks: NewBuildingCheck[]): Promise<string[]> {
        const user = this.getCurrentUser();
        if (checks.length === 0) return [];
        for (const check of checks) {
            if (!check.templateId) throw new Error('Välj en mall');
            if (!/^\d{4}-\d{2}-\d{2}$/.test(check.nextDueDate)) {
                throw new Error('Nästa datum måste anges som ÅÅÅÅ-MM-DD');
            }
        }
        const addedTime = formatDateForStorage(new Date());
        const updates: Record<string, unknown> = {};
        const keys: string[] = [];
        for (const check of checks) {
            const key = push(ref(this.db, `inspections/active/${buildingKey}`)).key!;
            const node: Record<string, unknown> = { ...check, addedBy: user.uid, addedTime };
            for (const [field, value] of Object.entries(node)) {
                if (value === undefined || value === '') delete node[field];
            }
            updates[`inspections/active/${buildingKey}/${key}`] = node;
            keys.push(key);
        }
        await update(ref(this.db), updates);
        checks.forEach((check, i) => {
            recordActivity('inspection.created', { buildingKey, targetKey: keys[i], label: check.label });
        });
        return keys;
    }

    /**
     * Edit a check (field-level update; null clears). templateId and componentKey cannot
     * change: to move a check, remove it and create a new one.
     */
    async updateBuildingCheck(buildingKey: string, checkKey: string, changes: BuildingCheckChanges): Promise<void> {
        const user = this.getCurrentUser();
        const updates: Record<string, unknown> = { ...changes };
        for (const key of ['label', 'buildingNotes', 'unitKey', 'unitLabel'] as const) {
            if (typeof updates[key] === 'string' && !(updates[key] as string).trim()) updates[key] = null;
        }
        for (const [key, value] of Object.entries(updates)) {
            if (value === undefined) delete updates[key];
        }
        updates.lastModifiedBy = user.uid;
        updates.lastModifiedTime = formatDateForStorage(new Date());
        await update(ref(this.db, `inspections/active/${buildingKey}/${checkKey}`), updates);
        recordActivity('inspection.updated', { buildingKey, targetKey: checkKey, label: changes.label || undefined });
    }

    /**
     * Quick completion from the web: photos are uploaded first under the completion's
     * `photos/` (the only part clients may write), then completeInspection writes the
     * record and moves the check's due date.
     */
    async completeBuildingCheck(input: QuickCompletionInput): Promise<void> {
        const callable = httpsCallable(getFunctions(undefined, 'europe-north1'), 'completeInspection');
        await callable({
            buildingKey: input.buildingKey,
            checkKey: input.checkKey,
            completedDate: input.completedDate,
            summaryComment: input.summaryComment,
            checkLabel: input.checkLabel,
            source: 'web',
            ...(input.measurementValue !== undefined && { measurement: { value: input.measurementValue } })
        });
        recordActivity('inspection.completed', { buildingKey: input.buildingKey, targetKey: input.checkKey, label: input.checkLabel });
    }

    /** Whether a completion of the check is already recorded on that day (one per day). */
    async hasCompletionOn(buildingKey: string, checkKey: string, date: string): Promise<boolean> {
        const snapshot = await get(ref(this.db, `inspections/completions/${buildingKey}/${checkKey}/${date}/completedBy`));
        return snapshot.exists();
    }

    /** Completions of one check in a building, newest first. */
    async loadCompletions(buildingKey: string, checkKey: string): Promise<DatedCheckCompletion[]> {
        const snapshot = await get(ref(this.db, `inspections/completions/${buildingKey}/${checkKey}`));
        if (!snapshot.exists()) return [];
        const data = snapshot.val() as Record<string, unknown>;
        return Object.entries(data)
            .filter(([, value]) => value && typeof value === 'object')
            .map(([date, value]) => ({ ...(value as CheckCompletion), date }))
            .sort((a, b) => b.date.localeCompare(a.date));
    }

    /**
     * Edit the interval and description of a Skickbedömning system template from
     * the Settings tab (admin or superadmin). Category, group and `system` are never touched.
     */
    async updateAssessmentTemplate(
        group: AssessmentGroupKey,
        changes: { interval: DateInterval; description: string }
    ): Promise<void> {
        await requireAdmin(this.db, 'ändra mallar för skickbedömning');
        if (!parseInterval(changes.interval)) {
            throw new Error('Intervallet måste vara minst 1');
        }
        const templateId = assessmentTemplateId(group);
        const templateRef = ref(this.db, `inspections/templates/${templateId}`);
        const snapshot = await get(templateRef);
        if (!snapshot.exists()) {
            throw new Error(`Mallen för ${ASSESSMENT_GROUPS[group].label} saknas`);
        }
        await update(templateRef, {
            interval: { value: Math.round(changes.interval.value), unit: changes.interval.unit },
            description: changes.description,
            updatedAt: formatDateForStorage(new Date())
        });
        recordActivity('settings.updated', { targetKey: templateId, label: snapshot.val()?.name });
    }

    /**
     * Move the next due date of checks in one update (draft Kontroller rows: the checks of
     * a template on the selected components); snoozes are cleared. Refused when one is gone.
     */
    async setNextDueDates(buildingKey: string, checkKeys: string[], nextDueDate: string): Promise<void> {
        this.getCurrentUser();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(nextDueDate) || nextDueDate < '2000-01-01') {
            throw new Error('Nästa datum måste anges som ÅÅÅÅ-MM-DD');
        }
        if (checkKeys.length === 0) return;
        const snapshots = await Promise.all(checkKeys.map(checkKey => get(ref(this.db, `inspections/active/${buildingKey}/${checkKey}`))));
        if (snapshots.some(snapshot => !snapshot.exists())) {
            throw new Error('Kontrollen hittades inte');
        }
        const updates: Record<string, unknown> = {};
        for (const checkKey of checkKeys) {
            const base = `inspections/active/${buildingKey}/${checkKey}`;
            updates[`${base}/nextDueDate`] = nextDueDate;
            updates[`${base}/snoozedUntil`] = null;
            updates[`${base}/snoozedBy`] = null;
            updates[`${base}/snoozedAt`] = null;
        }
        await update(ref(this.db), updates);
        checkKeys.forEach(checkKey => recordActivity('inspection.updated', { buildingKey, targetKey: checkKey }));
    }

    /** Remove a check from a building (admin or superadmin). Completions are kept. */
    async removeBuildingCheck(buildingKey: string, checkKey: string): Promise<void> {
        await this.removeBuildingChecks(buildingKey, [checkKey]);
    }

    /** Remove several checks of a building in one update (admin or superadmin). Completions are kept. */
    async removeBuildingChecks(buildingKey: string, checkKeys: string[]): Promise<void> {
        await requireAdmin(this.db, 'ta bort kontrollen');
        if (checkKeys.length === 0) return;
        const updates: Record<string, unknown> = {};
        for (const checkKey of checkKeys) updates[`inspections/active/${buildingKey}/${checkKey}`] = null;
        await update(ref(this.db), updates);
        checkKeys.forEach(checkKey => recordActivity('inspection.deleted', { buildingKey, targetKey: checkKey }));
    }
}

// Export singleton instance
export const buildingChecksApi = new BuildingChecksApi();
